import React, {
  Context,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import Joyride, { ACTIONS, EVENTS, STATUS, Step } from "react-joyride";
import { ClinicColors } from "fe-web-components/styles/clinicTheme";

export enum WalkthroughSections {
  WELCOME = "welcome",
  PATIENT_CARD = "patientCard",
}

export type ClinicWalkthroughContextApi = {
  section: WalkthroughSections;
  running: boolean;
  start: (stepIndex?: number) => void;
  stop: () => void;
  setSection: React.Dispatch<React.SetStateAction<WalkthroughSections>>;
};

export const walkThroughSteps: { [key in WalkthroughSections]: Step[] } = {
  [WalkthroughSections.WELCOME]: [
    {
      title: "New Case",
      content: "Create a new case using free text or Kahun's chatbot",
      placement: "top",
      target: ".new-case-button",
      disableBeacon: true,
    },
  ],
  [WalkthroughSections.PATIENT_CARD]: [
    {
      title: "Clinical Reasoning",
      content: "Expand to see the clinical reasoning for each DD",
      placement: "top",
      target: ".first-dd-item",
      disableBeacon: true,
      floaterProps: {
        disableAnimation: true,
      },
    },
    {
      title: "Clinical Grounding",
      content: "View the clinical evidence for Gen-AI generated statements",
      placement: "top",
      target: ".assessment-section i:first-of-type",
      disableBeacon: true,
      floaterProps: {
        disableAnimation: true,
      },
    },
    {
      title: "New Case",
      content: "Create a new case using free text or Kahun's chatbot",
      placement: "top",
      target: ".new-case-button",
      disableBeacon: true,
      floaterProps: {
        disableAnimation: true,
      },
    },
    {
      title: "Edit Case",
      content: "Edit patient summary and update the assessment",
      placement: "top",
      target: ".edit-summary",
      disableBeacon: true,
      floaterProps: {
        disableAnimation: true,
      },
    },
  ],
};

export const FINISHED_STEPS_KEY = "WalkthroughFinishedSteps";

const ClinicWalkthroughContext: Context<ClinicWalkthroughContextApi> = createContext<
  ClinicWalkthroughContextApi
>(null);
export const useClinicWalkthrough = () => useContext(ClinicWalkthroughContext);

export const ClinicWalkthroughProvider = ({ children }: { children?: any }) => {
  const [section, setSection] = useState<WalkthroughSections>(null);
  const [stepIndex, setStepIndex] = useState<number>(-1);
  const [running, setRunning] = useState<boolean>(false);
  const [activeSteps, setActiveSteps] = useState<Step[]>([]);

  const start = (newStep: number = 0) => {
    if (activeSteps.length === 0) return;
    setRunning(false);
    setTimeout(() => {
      setRunning((prevRunning) => {
        setStepIndex(newStep);
        return true;
      });
      console.log("started walkthrough");
    }, 1000);
  };

  const stop = () => {
    setRunning(false);
    console.log("stopped walkthrough");
  };

  useEffect(() => {
    let active = true;
    setRunning(false);
    if (section) {
      const finishedSteps = JSON.parse(
        localStorage.getItem(FINISHED_STEPS_KEY),
      ) || { steps: [] };
      const updatedSteps = walkThroughSteps[section].filter(
        (step) => !finishedSteps.steps.includes(step.target.toString()),
      );
      setActiveSteps(updatedSteps);
      if (updatedSteps?.length)
        setTimeout(() => {
          if (active) setRunning(true); //start walkthrough 2 seconds after section change
        }, 2000);
    }
    return () => {
      active = false;
    }; //cleanup
  }, [section]);

  const handleJoyrideCallback = (data: any) => {
    const { action, index, status, type, step: currStep } = data;
    if (
      action === ACTIONS.CLOSE ||
      [STATUS.FINISHED, STATUS.SKIPPED].includes(status)
    ) {
      stop();
    } else if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
    }
    if ([ACTIONS.NEXT, ACTIONS.CLOSE].includes(action) && currStep) {
      //add step to finishedSteps in local storage
      const finishedSteps = JSON.parse(
        localStorage.getItem(FINISHED_STEPS_KEY),
      ) || { steps: [] };
      localStorage.setItem(
        FINISHED_STEPS_KEY,
        JSON.stringify({
          steps: [...finishedSteps.steps, currStep.target],
        }),
      );
    }
  };

  return (
    <ClinicWalkthroughContext.Provider
      value={{
        section,
        setSection,
        start,
        stop,
        running,
      }}
    >
      {running && (
        <Joyride
          steps={activeSteps}
          run={running}
          stepIndex={stepIndex}
          callback={handleJoyrideCallback}
          disableScrolling
          continuous={true}
          locale={{ last: "OK" }}
          styles={{
            options: {
              primaryColor: ClinicColors.primary,
              zIndex: 1000,
              width: 250,
            },
            tooltip: {
              fontSize: 14,
            },
            tooltipContent: { padding: "10px 0 0" },
            tooltipTitle: { fontSize: 16 },
            buttonNext: { fontSize: 14 },
            buttonBack: { fontSize: 14 },
          }}
        />
      )}
      {children}
    </ClinicWalkthroughContext.Provider>
  );
};
